import { HREFTemplated, Self, SelfCreatedUpdatedBy, SelfTemplated } from "./link.model";
import { CreatedUpdated, HasId, NameDescSortId } from "./properties.model";
import { IAngularEvent, IRootScopeService } from "angular";
import { Log } from "ng/src/app/logs/log.model";
import { CreatedUpdatedBy, User } from "ng/src/app/users/shared/user.model";
import { HALPageDetails } from "ng/src/app/shared/model/app.model";
import { IRouteEventRoute } from "app/shared/model/route.model";
import { Observable } from "rxjs";
import { Utils } from "ng/src/app/core/utils";
import { HelpSection } from "ng/src/app/core/help-section";

export interface AuditedEntity extends CreatedUpdated {
	createdBy: CreatedUpdatedBy;
	updatedBy: CreatedUpdatedBy;
}

export interface Style extends NameDescSortId {
	styleGroup?: string;
	_links: SelfTemplated;
}

export interface ComplexObject extends AuditedEntity {
	id: number;
	_links: SelfCreatedUpdatedBy & {
		log: HREFTemplated;
		currentVersion: HREFTemplated;
	};
}

export interface ComplexObjectVersion {
	activeRevision: boolean;
	published?: string;
	saveInProgress: boolean;
	versionNote?: string;
	isDraft: boolean;
	complexObject: BaseEntity
}

export interface SortableModel {
	sortOrder?: number,
	prevItemId?: number
}

export interface ComplexObjectModel {
	complexObject: string
}

export interface TextListItem {
	text: string;
	required: boolean;
}

export interface Comment extends BaseEntity {
	comment: string;
	safeHtmlComment: string;
}

export interface BaseEntity extends AuditedEntity, HasId {
	_links: Self
}

export interface LogBookRootScopeService extends IRootScopeService {
	[key:string]: any
	selectedLog?: Log
	user?: User,
	versionMismatch?: boolean
	$on: <T extends AngularJsEventType>(on: T, listener: AngularJsEventListener[T]) => () => void
}

export class LogBookRootScopeService {
	static stopRouteChange(event: AngularJsEvent<'$routeChangeStart'>) {
		event.preventDefault();
		event.currentScope.$root.$emit('$routeChangeError');
	}
	
	static observeEvent<T extends AngularJsEventType>(scope: Pick<LogBookRootScopeService, '$on'>, on: T) {
		return new Observable<Parameters<AngularJsEventListener[T]>>(subscriber => {
			const canceller = scope.$on<''>(on as any, function(event: IAngularEvent, ...params: any[]) {
				subscriber.next([event, ...params] as any);
			});
			subscriber.add(() => canceller());
		});
	}
}

export interface Controller {
	[property: string]: any
}

export interface PagedArray<T> extends Array<T> {
	page: HALPageDetails
}

export class PagedArray<T> {
	public static isPagedArray(value: any): value is PagedArray<any> {
		return Array.isArray(value) && ('page' in (value as PagedArray<any>));
	}
	
	public static from<T>(src: PagedArray<T>): PagedArray<T>;
	public static from<T>(src: T[], page: HALPageDetails): PagedArray<T>
	public static from<T>(src: T[] | PagedArray<T>, page?: HALPageDetails): PagedArray<T> {
		const newArr = Array.from(src) as PagedArray<T>;
		
		if (PagedArray.isPagedArray(src)) {
			page = src.page;
		}
		
		newArr.page = Utils.deepCopy(page!);
		
		return newArr;
	}
}

export interface PagedEntity<T extends BaseEntity> extends PagedArray<T> {}

export class PagedEntity<T extends BaseEntity> {
	/**
	 * Creates a new PagedEntity from the source provided along with page information
	 * @param src - Source response array
	 * @param page - Source Page Details
	 */
	static from<T extends BaseEntity>(src: T[], page: HALPageDetails): PagedEntity<T> {
		return PagedArray.from(src, page);
	}
	
}
type AngularJsEventType = string | keyof AngularJsEventListener;
type AngularJsEvent <T extends AngularJsEventType> = IAngularEvent;

interface AngularJsEventListener<T extends AngularJsEventType = ''> {
	$routeChangeStart: (event: AngularJsEvent<'$routeChangeStart'>, next: IRouteEventRoute, current: IRouteEventRoute) => any
	$routeChangeSuccess: (event: AngularJsEvent<'$routeChangeSuccess'>, current: IRouteEventRoute, previous?: IRouteEventRoute) => any
	$routeChangeError: (event: AngularJsEvent<'$routeChangeError'>, current: IRouteEventRoute, previous: IRouteEventRoute, rejection: IRouteEventRoute) => any
	$routeUpdate: (event: AngularJsEvent<'$routeUpdate'>, current: IRouteEventRoute) => any
	$locationChangeStart: (event: AngularJsEvent<'$routeUpdate'>, newUrl: string, oldUrl?: string, newState?: string, oldState?: string) => any
	$locationChangeSuccess: (event: AngularJsEvent<'$locationChangeSuccess'>, newUrl: string, oldUrl?: string, newState?: string, oldState?: string) => any
	logoutStart: (event: AngularJsEvent<any>, logout: () => void) => any
	helpOpening: (event: AngularJsEvent<any>, helpSection: HelpSection) => any
	[key: string]: (event: AngularJsEvent<T>, ...params: any[]) => any
}